import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface SharedPackingItem {
  id: string;
  name: string;
  category: string;
  essential: boolean;
  packed: boolean;
  quantity?: number;
}

/**
 * Persists a packing list against a share code so invited buddies
 * can open /join/:code and edit the same list.
 *
 * Writes are merged against the row currently stored in the database so a
 * collaborator's `packed` toggles are never clobbered by the owner's
 * (potentially stale) local copy.
 */
export function useSharedPackingList(shareCode: string) {
  const { user } = useAuth();
  const [listId, setListId] = useState<string | null>(null);
  const listIdRef = useRef<string | null>(null);
  // Snapshot of what we last pushed/pulled, used for a 3-way merge.
  const lastKnownRef = useRef<Map<string, boolean>>(new Map());

  const setList = (id: string | null) => {
    listIdRef.current = id;
    setListId(id);
  };

  const snapshot = (items: SharedPackingItem[]) => {
    lastKnownRef.current = new Map(items.map((i) => [i.id, i.packed]));
  };

  /**
   * Merge local items with the remote copy: for each item, if the local
   * `packed` value changed since our last sync the local value wins,
   * otherwise the remote (collaborator) value wins.
   */
  const mergeWithRemote = (
    local: SharedPackingItem[],
    remote: SharedPackingItem[]
  ): SharedPackingItem[] => {
    const remoteById = new Map(remote.map((i) => [i.id, i]));
    return local.map((item) => {
      const remoteItem = remoteById.get(item.id);
      if (!remoteItem) return item;
      const lastKnown = lastKnownRef.current.get(item.id);
      const localChanged = lastKnown !== undefined && lastKnown !== item.packed;
      return localChanged ? item : { ...item, packed: remoteItem.packed };
    });
  };

  const ensureList = useCallback(
    async (payload: {
      destination: string;
      country?: string;
      tripType?: string;
      tripId?: string;
      items: SharedPackingItem[];
    }) => {
      if (!user) return null;

      // Never overwrite an existing row's items on ensure — only create it.
      const { data: existing } = await supabase
        .from('shared_packing_lists')
        .select('id, items')
        .eq('share_code', shareCode)
        .maybeSingle();

      if (existing?.id) {
        setList(existing.id);
        snapshot(
          Array.isArray(existing.items)
            ? (existing.items as unknown as SharedPackingItem[])
            : payload.items
        );
        return existing.id;
      }

      const { data, error } = await supabase
        .from('shared_packing_lists')
        .insert({
          owner_id: user.id,
          share_code: shareCode,
          destination: payload.destination,
          country: payload.country ?? null,
          trip_type: payload.tripType ?? null,
          trip_id: payload.tripId ?? null,
          items: payload.items as unknown as never,
        })
        .select('id')
        .maybeSingle();

      if (error) {
        console.error('Failed to create shared packing list:', error);
        return null;
      }

      snapshot(payload.items);
      setList(data?.id ?? null);
      return data?.id ?? null;
    },
    [user, shareCode]
  );

  const syncItems = useCallback(async (items: SharedPackingItem[]) => {
    const id = listIdRef.current;
    if (!id) return;

    const { data: current, error: readError } = await supabase
      .from('shared_packing_lists')
      .select('items')
      .eq('id', id)
      .maybeSingle();

    if (readError) {
      console.error('Failed to read shared packing list:', readError);
      return;
    }

    const remote = Array.isArray(current?.items)
      ? (current!.items as unknown as SharedPackingItem[])
      : [];
    const merged = mergeWithRemote(items, remote);

    // Skip no-op writes: they would echo back through realtime and loop forever.
    if (JSON.stringify(merged) === JSON.stringify(remote)) {
      snapshot(merged);
      return;
    }

    const { error } = await supabase
      .from('shared_packing_lists')
      .update({ items: merged as unknown as never })
      .eq('id', id);

    if (error) {
      console.error('Failed to sync shared packing list:', error);
      return;
    }
    snapshot(merged);
  }, []);


  /** Records incoming realtime state so it is not treated as a local change. */
  const acknowledgeRemote = useCallback((items: SharedPackingItem[]) => {
    snapshot(items);
  }, []);

  return { ensureList, syncItems, acknowledgeRemote, listId, listIdRef };
}

/** Subscribes to realtime updates for a shared list. */
export function useSharedListRealtime(
  listId: string | null,
  onChange: (items: SharedPackingItem[]) => void
) {
  useEffect(() => {
    if (!listId) return;
    const channel = supabase
      .channel(`shared-packing:${listId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'shared_packing_lists',
          filter: `id=eq.${listId}`,
        },
        (payload) => {
          const items = (payload.new as { items?: SharedPackingItem[] })?.items;
          if (Array.isArray(items)) onChange(items);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [listId, onChange]);
}
